import { prefersReducedMotion } from "@js/parts/prefersReducedMotion";

export default (config = {}) => ({
  start: config.start ?? 0,
  end: config.end ?? 100,
  duration: config.duration ?? 3000, // milliseconds
  decimals: config.decimals ?? 0,
  separator: config.separator ?? ",",
  autoStart: config.autoStart ?? true,
  easing: config.easing ?? "easeOutCubic", // easeOutCubic, easeInOutCubic, linear

  // State
  current: 0,
  isRunning: false,
  startTime: null,
  animationFrame: null,

  init() {
    this.current = this.start;

    if (prefersReducedMotion()) {
      this.current = this.end;
      return;
    }

    if (this.autoStart) {
      this.startCounter();
    }
  },

  // Easing functions
  easingFunctions: {
    linear: (t) => t,
    easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
    easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  },

  getEasedProgress(progress) {
    const easingFn = this.easingFunctions[this.easing] || this.easingFunctions.linear;
    return easingFn(progress);
  },

  formatNumber(num) {
    // Round to specified decimals
    const rounded = Math.round(num * Math.pow(10, this.decimals)) / Math.pow(10, this.decimals);

    // Split into integer and decimal parts
    const parts = rounded.toFixed(this.decimals).split(".");

    // Add thousand separators to integer part
    if (this.separator) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.separator);
    }

    // Return combined result
    return this.decimals > 0 ? parts.join(".") : parts[0];
  },

  animate() {
    if (!this.isRunning) return;

    const now = performance.now();
    const elapsed = now - this.startTime;
    const progress = Math.min(elapsed / this.duration, 1);

    // Apply easing
    const easedProgress = this.getEasedProgress(progress);

    // Calculate current value
    this.current = this.start + (this.end - this.start) * easedProgress;

    // Continue or stop
    if (progress < 1) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
    } else {
      this.current = this.end;
      this.isRunning = false;
    }
  },

  startCounter() {
    if (this.isRunning) return;

    if (prefersReducedMotion()) {
      this.current = this.end;
      return;
    }

    this.isRunning = true;
    this.startTime = performance.now();
    this.animationFrame = requestAnimationFrame(() => this.animate());
  },

  reset() {
    this.stopCounter();
    this.current = this.start;
  },

  stopCounter() {
    this.isRunning = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  },

  destroy() {
    this.stopCounter();
  },

  // Getter for formatted display value
  get displayValue() {
    return this.formatNumber(this.current);
  },
});
